// Country prefixes the UI currently accepts. Order matters: more specific
// prefixes (longer numeric strings like "+380") must come before shorter ones
// that they would otherwise shadow (e.g. "+38").
const KNOWN_PREFIXES = ["+420", "+421", "+380", "+49", "+43", "+48"] as const;

const PHONE_GROUPS: Record<(typeof KNOWN_PREFIXES)[number], number[]> = {
  "+420": [3, 3, 3],
  "+421": [3, 3, 3],
  "+380": [2, 3, 2, 2],
  "+49": [4, 7],
  "+43": [3, 7],
  "+48": [3, 3, 3],
};

const matchPrefix = (value: string) =>
  KNOWN_PREFIXES.find(p => value.startsWith(p));

// Normalize an incoming phone number into +<country><subscriber> form so the
// IMask-driven phone input doesn't mangle bare 9-digit numbers (it would
// otherwise eat leading digits while searching for a 0/1 to fill the prefix).
export const normalizePhoneNumber = (
  phoneNumber: string | undefined | null,
): string | undefined => {
  if (!phoneNumber) {
    return undefined;
  }

  const stripped = phoneNumber.replace(/\s/g, "");
  if (!stripped) {
    return undefined;
  }

  if (matchPrefix(stripped)) {
    return stripped;
  }

  if (stripped.startsWith("00")) {
    const withPlus = `+${stripped.slice(2)}`;
    if (matchPrefix(withPlus)) {
      return withPlus;
    }
  }

  if (/^\d+$/.test(stripped)) {
    const candidate = `+${stripped}`;
    if (matchPrefix(candidate)) {
      return candidate;
    }
    if (stripped.length === 9) {
      return `+420${stripped}`;
    }
  }

  return stripped;
};

export const formatPhoneNumber = (phoneNumber: string | undefined | null) => {
  const normalized = normalizePhoneNumber(phoneNumber);
  if (!normalized) {
    return "";
  }

  const prefix = matchPrefix(normalized);
  if (!prefix) {
    return normalized;
  }

  const rest = normalized.slice(prefix.length);
  const groups = PHONE_GROUPS[prefix];
  const parts: string[] = [prefix];

  let offset = 0;
  for (const len of groups) {
    const piece = rest.slice(offset, offset + len);
    if (!piece) {
      break;
    }
    parts.push(piece);
    offset += len;
  }

  const leftover = rest.slice(offset);
  if (leftover) {
    parts.push(leftover);
  }

  return parts.join(" ");
};
